import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageSquare, LogOut, Loader2, User, Settings, Power, RefreshCw, Save, Wifi, WifiOff, CreditCard, Shield, Plus, ChevronDown, Bot, Trash2, ArrowLeft, Car, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import ChatSimulator from '../components/ChatSimulator';
import RecentMessages from '../components/RecentMessages';
import PromptHistory from '../components/PromptHistory';
import BotMetrics from '../components/BotMetrics';
import NotificationBell from '../components/NotificationBell';
import ConversationPanel from '../components/ConversationPanel';
import MenuEditor from '../components/MenuEditor';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Bot Selector Component ----
const BotSelector = ({ bots, selectedBot, onSelect, maxBots, onCreateBot, isCreating, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const canCreate = isAdmin || bots.length < maxBots;
  const planLabel = maxBots >= 999 ? 'Admin (ilimitado)' : `${bots.length}/${maxBots} asistentes`;

  return (
    <div className="relative" ref={ref} data-testid="bot-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-indigo-300 transition-all min-w-[220px]"
        data-testid="bot-selector-trigger"
      >
        <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Bot className="w-4 h-4 text-indigo-600" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{selectedBot?.bot_name || 'Seleccionar Asistente'}</p>
          <p className="text-xs text-gray-500">{planLabel}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="bot-selector-dropdown">
          <div className="p-2 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 px-2 py-1">TUS ASISTENTES</p>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {bots.map((b) => (
              <button
                key={b.bot_id}
                onClick={() => { onSelect(b); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors ${selectedBot?.bot_id === b.bot_id ? 'bg-indigo-50' : ''}`}
                data-testid={`bot-option-${b.bot_id}`}
              >
                <div className={`w-2 h-2 rounded-full ${b.status === 'connected' ? 'bg-emerald-500' : b.status === 'sleeping' ? 'bg-amber-400' : 'bg-gray-300'}`} />
                <div className="text-left flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{b.bot_name || 'Asistente'}</p>
                  <p className="text-xs text-gray-500">{b.bot_id}</p>
                </div>
                {selectedBot?.bot_id === b.bot_id && (
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
                )}
              </button>
            ))}
          </div>
          {canCreate && (
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => { onCreateBot(); setOpen(false); }}
                disabled={isCreating}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50"
                data-testid="create-new-bot-dropdown-btn"
              >
                {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crear Nuevo Asistente
              </button>
            </div>
          )}
          {!canCreate && (
            <div className="p-3 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-500">Limite de asistentes alcanzado</p>
              <p className="text-xs text-indigo-600 font-medium">Actualiza tu plan para mas asistentes</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Main Dashboard ----
const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Multi-bot state
  const [botList, setBotList] = useState([]);
  const [maxBots, setMaxBots] = useState(1);
  const [selectedBot, setSelectedBot] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' = bot cards, 'config' = bot configuration
  const [isDeleting, setIsDeleting] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return !!(sp.get('session_id') && (sp.get('trial_started') || sp.get('payment')));
  });

  const isAdmin = user?.role === 'admin';
  const isPartner = user?.role === 'partner';
  const canCreate = isAdmin || botList.length < maxBots;
  const hasBots = botList.length > 0;

  // Partners use their own simplified dashboard at /partner
  useEffect(() => {
    if (isPartner) navigate('/partner', { replace: true });
  }, [isPartner, navigate]);

  // Config form fields
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [autoReplies, setAutoReplies] = useState([{ key: '', value: '' }]);
  const [botPrompt, setBotPrompt] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [services, setServices] = useState('');
  const [pricing, setPricing] = useState('');
  const [workingHours, setWorkingHours] = useState('');
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [aiProvider, setAiProvider] = useState('openai');
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState('disconnected');
  const [menuItems, setMenuItems] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [notificationWhatsapp, setNotificationWhatsapp] = useState('');
  const [userPlanId, setUserPlanId] = useState(null);
  // Kit lock/unlock state (per plan). Fetched from /api/kits/status.
  // Shape: { plan, included_kit_used, unlock_price_usd, kits: [{slug,name,unlocked,reason}] }
  const [kitsStatus, setKitsStatus] = useState(null);
  const [kitModal, setKitModal] = useState(null); // { slug, name, reason }
  const [kitConfirmChecked, setKitConfirmChecked] = useState(false);
  const [unlockingKit, setUnlockingKit] = useState(false);
  const qrPollRef = useRef(null);
  const statusPollRef = useRef(null);

  // Load bot form from a bot object
  const loadBotForm = useCallback((bot) => {
    if (!bot) return;
    setWelcomeMessage(bot.welcome_message || '');
    setBotPrompt(bot.bot_prompt || '');
    setBusinessName(bot.business_name || '');
    setServices(bot.services || '');
    setPricing(bot.pricing || '');
    setWorkingHours(bot.working_hours || '');
    setHasApiKey(bot.has_api_key || false);
    setApiKeyMasked(bot.openai_api_key_masked || '');
    setAiProvider(bot.ai_provider || 'openai');
    setOpenaiApiKey('');
    setSubscriptionActive(bot.subscription_active !== false);
    setSubscriptionMessage(bot.subscription_message || '');
    setMenuItems(bot.menu_items || []);
    setShippingCost(bot.shipping_cost || 0);
    setNotificationWhatsapp(bot.notification_whatsapp || '');
    const replies = Object.entries(bot.auto_replies || {}).map(([key, value]) => ({ key, value }));
    setAutoReplies(replies.length > 0 ? replies : [{ key: '', value: '' }]);
  }, []);

  // Fetch bot list
  const fetchBotList = useCallback(async () => {
    try {
      const [botsRes, kitsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bot/list`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/kits/status`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      const data = botsRes.data;
      setBotList(data.bots || []);
      setMaxBots(data.max_bots || 1);
      setUserPlanId(data.plan_id || null);
      if (kitsRes.data) setKitsStatus(kitsRes.data);
      return data.bots || [];
    } catch (error) {
      console.error('Failed to fetch bot list:', error);
      return [];
    }
  }, []);

  // Route the user to a kit config page — but intercept if the kit is
  // locked so we can prompt for a plan upgrade or a $99 unlock instead of
  // dropping the user on a page that will 403 on their first save.
  const navigateToKit = useCallback((path, slug) => {
    const kit = kitsStatus?.kits?.find(k => k.slug === slug);
    if (kit && !kit.unlocked) {
      setKitConfirmChecked(false);
      setKitModal({ slug: kit.slug, name: kit.name, reason: kit.reason });
      return;
    }
    navigate(path);
  }, [kitsStatus, navigate]);

  const handleUnlockKit = useCallback(async () => {
    if (!kitModal) return;
    setUnlockingKit(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/kits/${kitModal.slug}/create-unlock-order`,
        {},
        { withCredentials: true },
      );
      if (data.approve_url) {
        // Store the pending order so we can capture it on return.
        sessionStorage.setItem('pending_kit_unlock', JSON.stringify({
          slug: kitModal.slug, order_id: data.order_id,
        }));
        window.location.href = data.approve_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar el pago');
      setUnlockingKit(false);
    }
  }, [kitModal]);

  // Fetch status for a specific bot
  const fetchBotStatus = useCallback(async (botId) => {
    if (!botId) return null;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/bot/status?bot_id=${botId}`, { withCredentials: true });
      if (data.bot) {
        loadBotForm(data.bot);
        return data;
      }
    } catch (error) {
      console.error('Failed to fetch bot status:', error);
    }
    return null;
  }, [loadBotForm]);

  // Select a bot
  const handleSelectBot = useCallback(async (bot) => {
    stopPolling();
    setQrDataUrl(null);
    setWhatsappStatus('disconnected');
    setSelectedBot(bot);
    loadBotForm(bot);
    setViewMode('config');
    await fetchBotStatus(bot.bot_id);
  }, [loadBotForm, fetchBotStatus]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      const bots = await fetchBotList();
      const targetBotId = searchParams.get('bot_id');
      const targetView = searchParams.get('view');
      if (targetBotId && bots.length > 0) {
        const targetBot = bots.find(b => b.bot_id === targetBotId);
        if (targetBot) {
          setSelectedBot(targetBot);
          loadBotForm(targetBot);
          if (targetView === 'config') {
            setViewMode('config');
            await fetchBotStatus(targetBot.bot_id);
          }
          // Clean query params
          navigate('/dashboard', { replace: true });
          return;
        }
      }

      // Check for pending prompt from Alex (saved before registration)
      const pendingPrompt = localStorage.getItem('pending_prompt');
      if (pendingPrompt) {
        localStorage.removeItem('pending_prompt');
        try {
          const res = await axios.post(`${BACKEND_URL}/api/bot/apply-prompt`, { prompt: pendingPrompt }, { withCredentials: true });
          if (res.data.success) {
            toast.success('Prompt de Alex aplicado a tu asistente!');
            const updatedBots = await fetchBotList();
            const newBot = updatedBots.find(b => b.bot_id === res.data.bot_id);
            if (newBot) {
              setSelectedBot(newBot);
              loadBotForm(newBot);
              setViewMode('config');
              await fetchBotStatus(newBot.bot_id);
            }
            return;
          }
        } catch (e) {
          console.error('Error applying pending prompt:', e);
        }
      }

      if (bots.length > 0) {
        setViewMode('list');
      }
    };
    init();
    return () => { stopPolling(); };
  }, []);

  // Handle Stripe return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    const trialStarted = searchParams.get('trial_started');

    if (sessionId && trialStarted === 'true') {
      // Return from trial checkout
      const pollTrial = async (attempts = 0) => {
        if (attempts >= 8) { toast.error('No se pudo verificar el trial. Intenta recargar la pagina.'); setVerifyingPayment(false); return; }
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/billing/verify-trial/${sessionId}`, { withCredentials: true });
          if (data.status === 'complete' && data.trial_active) {
            toast.success(`Prueba gratis de ${data.trial_days} dias activada! Tu plan ${data.plan_id} esta listo.`);
            setVerifyingPayment(false);
            fetchBotList();
            navigate('/dashboard', { replace: true });
            return;
          }
          if (data.status === 'expired') { toast.error('La sesion de pago expiro.'); setVerifyingPayment(false); return; }
        } catch (e) {}
        setTimeout(() => pollTrial(attempts + 1), 2000);
      };
      pollTrial();
    } else if (sessionId && payment === 'success') {
      // Return from regular checkout
      const poll = async (attempts = 0) => {
        if (attempts >= 8) { setVerifyingPayment(false); return; }
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/billing/checkout-status/${sessionId}`, { withCredentials: true });
          if (data.payment_status === 'paid') {
            toast.success('Pago exitoso! Tu suscripcion esta activa.');
            setVerifyingPayment(false);
            fetchBotList();
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch (e) {}
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
  }, [searchParams, fetchBotList, navigate]);

  // ── PayPal kit-unlock return handler ─────────────────────────────────
  // When the user returns from PayPal with ?unlock_success=1&kit=<slug>,
  // capture the pending order server-side. On success, the kit is added
  // to active_kits and the dashboard refreshes lock state.
  useEffect(() => {
    const unlockSuccess = searchParams.get('unlock_success');
    const unlockKit = searchParams.get('kit');
    const unlockCancelled = searchParams.get('unlock_cancelled');
    if (unlockCancelled === '1') {
      toast.info('Compra cancelada. El kit sigue bloqueado.');
      sessionStorage.removeItem('pending_kit_unlock');
      navigate('/dashboard', { replace: true });
      return;
    }
    if (unlockSuccess !== '1' || !unlockKit) return;
    const pendingRaw = sessionStorage.getItem('pending_kit_unlock');
    if (!pendingRaw) {
      toast.error('No se encontró la orden pendiente. Contacta soporte si el cargo se completó.');
      navigate('/dashboard', { replace: true });
      return;
    }
    let pending;
    try { pending = JSON.parse(pendingRaw); } catch { return; }
    if (!pending?.order_id || pending.slug !== unlockKit) {
      sessionStorage.removeItem('pending_kit_unlock');
      return;
    }
    (async () => {
      try {
        await axios.post(
          `${BACKEND_URL}/api/kits/${unlockKit}/capture-unlock-order`,
          { order_id: pending.order_id },
          { withCredentials: true },
        );
        toast.success(`Kit ${unlockKit} desbloqueado. ¡Ya puedes configurarlo!`);
        sessionStorage.removeItem('pending_kit_unlock');
        fetchBotList();
        navigate('/dashboard', { replace: true });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'PayPal no confirmó el pago');
        sessionStorage.removeItem('pending_kit_unlock');
      }
    })();
  }, [searchParams, fetchBotList, navigate]);

  const startPolling = useCallback((botId) => {
    let pollFailCount = 0;

    // Fetch QR immediately, then poll every 2s
    const fetchQR = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/bot/whatsapp-qr?bot_id=${botId}`, { withCredentials: true });
        if (data.qr) {
          setQrDataUrl(data.qr);
          setWhatsappStatus('waiting_qr');
        }
      } catch (e) {}
    };

    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/bot/whatsapp-status?bot_id=${botId}`, { withCredentials: true });
        if (data.hasQR) setWhatsappStatus('waiting_qr');
        if (data.connected) {
          stopPolling();
          setQrDataUrl(null);
          setWhatsappStatus('connected');
          toast.success('WhatsApp conectado exitosamente!');
          const bots = await fetchBotList();
          const updated = bots.find(b => b.bot_id === botId);
          if (updated) { setSelectedBot(updated); loadBotForm(updated); }
        } else if (data.status === 'disconnected' && !data.hasQR) {
          pollFailCount++;
          if (pollFailCount > 20) {
            stopPolling();
            setQrDataUrl(null);
            setWhatsappStatus('disconnected');
            toast.error('No se pudo conectar a WhatsApp. Intenta de nuevo.');
          }
        } else {
          pollFailCount = 0; // Reset if we get a valid non-disconnected status
        }
      } catch (e) {}
    };

    // First fetch after 500ms (give Baileys time to start)
    setTimeout(fetchQR, 500);
    setTimeout(fetchQR, 1500);
    setTimeout(fetchQR, 3000);

    // Then continuous polling
    qrPollRef.current = setInterval(fetchQR, 2500);
    statusPollRef.current = setInterval(fetchStatus, 3000);
  }, [fetchBotList, loadBotForm]);

  const stopPolling = useCallback(() => {
    if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
    if (statusPollRef.current) { clearInterval(statusPollRef.current); statusPollRef.current = null; }
  }, []);

  const handleCreateBot = () => {
    // Open the bot-type selection modal instead of creating directly
    setShowCreateTypeModal(true);
  };

  const confirmCreateBot = async (botType) => {
    setShowCreateTypeModal(false);
    setIsCreating(true);
    try {
      const defaultName = botType === 'kit' ? 'Kit AutoBot' : botType === 'garzon' ? 'GarzónBot' : botType === 'inmobiliaria' ? 'Kit Inmobiliario' : botType === 'legal' ? 'Renata' : botType === 'store' ? 'StoreBot' : 'Mi Asistente';
      const { data } = await axios.post(
        `${BACKEND_URL}/api/bot/create`,
        { name: defaultName, bot_type: botType },
        { withCredentials: true }
      );
      if (botType === 'kit') {
        toast.success('Kit AutoBot creado! Configura tu concesionaria en la pagina Kit AutoBot.');
      } else if (botType === 'garzon') {
        toast.success('GarzónBot creado! Configura tu restaurante en la pagina GarzónBot.');
      } else if (botType === 'inmobiliaria') {
        toast.success('Kit Inmobiliario creado! Configura tu corredora en la pagina Kit Inmobiliario.');
      } else if (botType === 'legal') {
        toast.success('Kit Legal creado! Configura tu estudio en la pagina Kit Legal.');
      } else if (botType === 'store') {
        toast.success('StoreBot creado! Configura tu tienda en la pagina Kit StoreBot.');
      } else {
        toast.success('Asistente creado! Configura tu asistente y conecta a WhatsApp.');
      }
      const bots = await fetchBotList();
      const newBot = bots.find(b => b.bot_id === data.bot_id);
      if (newBot) { handleSelectBot(newBot); }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al crear el asistente';
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBot = async (e, botId, botName) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminar "${botName || 'Asistente'}"? Se borraran todas sus conversaciones y configuracion. Esta accion no se puede deshacer.`)) return;
    setIsDeleting(botId);
    try {
      await axios.delete(`${BACKEND_URL}/api/bot/${botId}`, { withCredentials: true });
      toast.success('Asistente eliminado');
      const bots = await fetchBotList();
      if (selectedBot?.bot_id === botId) {
        setSelectedBot(null);
        setViewMode('list');
      }
      if (bots.length === 0) setViewMode('list');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleBackToList = () => {
    stopPolling();
    setSelectedBot(null);
    setQrDataUrl(null);
    setViewMode('list');
    fetchBotList();
  };

  const handleStartWhatsApp = async () => {
    if (!selectedBot) return;
    setIsConnecting(true);
    setQrDataUrl(null);
    setWhatsappStatus('connecting');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/bot/start-whatsapp?bot_id=${selectedBot.bot_id}`, {}, { withCredentials: true });
      if (data.success) {
        toast.success('Conectando a WhatsApp...');
        startPolling(selectedBot.bot_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al conectar');
      setWhatsappStatus('disconnected');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleStopWhatsApp = async () => {
    if (!selectedBot) return;
    stopPolling();
    try {
      await axios.post(`${BACKEND_URL}/api/bot/stop-whatsapp?bot_id=${selectedBot.bot_id}`, {}, { withCredentials: true });
      setWhatsappStatus('disconnected');
      setQrDataUrl(null);
      toast.success('WhatsApp desconectado');
      const bots = await fetchBotList();
      const updated = bots.find(b => b.bot_id === selectedBot.bot_id);
      if (updated) { setSelectedBot(updated); loadBotForm(updated); }
    } catch (error) {
      toast.error('Error al desconectar');
    }
  };

  const handleToggleBot = async () => {
    if (!selectedBot) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/bot/toggle?bot_id=${selectedBot.bot_id}`, {}, { withCredentials: true });
      setSelectedBot(data);
      loadBotForm(data);
      toast.success(data.is_enabled ? 'Asistente habilitado' : 'Asistente deshabilitado');
      fetchBotList();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedBot) return;
    setIsSaving(true);
    try {
      const autoRepliesObj = {};
      autoReplies.forEach(({ key, value }) => { if (key.trim() && value.trim()) autoRepliesObj[key.trim()] = value.trim(); });
      const payload = {
        welcome_message: welcomeMessage, auto_replies: autoRepliesObj,
        bot_prompt: botPrompt, business_name: businessName, services, pricing, working_hours: workingHours,
        ai_provider: aiProvider,
        menu_items: menuItems,
        shipping_cost: shippingCost,
        notification_whatsapp: notificationWhatsapp,
      };
      if (openaiApiKey.trim()) payload.openai_api_key = openaiApiKey;
      const { data } = await axios.post(`${BACKEND_URL}/api/bot/config?bot_id=${selectedBot.bot_id}`, payload, { withCredentials: true });
      setSelectedBot(data);
      loadBotForm(data);
      toast.success('Configuracion guardada!');
      fetchBotList();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const addAutoReply = () => setAutoReplies([...autoReplies, { key: '', value: '' }]);
  const removeAutoReply = (i) => setAutoReplies(autoReplies.filter((_, idx) => idx !== i));
  const updateAutoReply = (i, field, val) => { const u = [...autoReplies]; u[i][field] = val; setAutoReplies(u); };

  const handleLogout = async () => { stopPolling(); await logout(); navigate('/login'); };

  const botStatus = selectedBot?.status || 'no_bot';
  const isConnected = whatsappStatus === 'connected' || botStatus === 'connected';
  const isWaitingQR = whatsappStatus === 'waiting_qr' || botStatus === 'waiting_for_scan';

  const getStatusColor = () => {
    if (isConnected) return 'text-emerald-600';
    if (isWaitingQR) return 'text-amber-500';
    return 'text-gray-500';
  };
  const getStatusText = () => {
    if (isConnected) return 'Conectado';
    if (isWaitingQR) return 'Esperando Escaneo QR';
    if (whatsappStatus === 'connecting') return 'Conectando...';
    if (botStatus === 'sleeping') return 'Dormido';
    if (botStatus === 'disconnected') return 'Desconectado';
    return 'Sin Asistente';
  };
  const getStatusDotClass = () => {
    if (isConnected) return 'connected';
    if (isWaitingQR) return 'waiting';
    return 'disconnected';
  };

  if (verifyingPayment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="verifying-payment-screen">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-950 mb-2">Verificando tu pago...</h2>
          <p className="text-gray-500">Esto solo toma unos segundos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Kit Lock Modal — shown when user clicks a locked kit */}
      {kitModal && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kit-lock-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-950">{kitModal.name} está bloqueado</h3>
                <p className="text-sm text-gray-500">
                  {kitModal.reason === 'requires_business' && 'Los kits requieren el plan Business.'}
                  {kitModal.reason === 'included_available' && 'Al activar este kit consumirás el kit incluido gratis en tu plan Business.'}
                  {kitModal.reason === 'requires_unlock' && `Ya usaste tu kit incluido. Puedes desbloquear ${kitModal.name} por $${kitsStatus?.unlock_price_usd || 99} USD (pago único).`}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 mt-6">
              {kitModal.reason === 'requires_business' && (
                <Button onClick={() => { setKitModal(null); navigate('/plans'); }} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="kit-lock-upgrade-btn">
                  Ver planes Business
                </Button>
              )}
              {kitModal.reason === 'included_available' && (
                <>
                  <label
                    className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
                    data-testid="kit-lock-confirm-label"
                  >
                    <input
                      type="checkbox"
                      checked={kitConfirmChecked}
                      onChange={(e) => setKitConfirmChecked(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer"
                      data-testid="kit-lock-confirm-checkbox"
                    />
                    <span className="text-xs text-amber-900 leading-snug">
                      Confirmo que quiero usar mi <b>kit incluido gratis</b> en <b>{kitModal.name}</b>.
                      Después de esto, los otros kits requerirán $99 USD c/u para desbloquearse.
                    </span>
                  </label>
                  <Button
                    onClick={() => { const path = { kit:'/kit', garzon:'/garzon', inmobiliaria:'/inmobiliaria', legal:'/kit-legal', store:'/kit-store' }[kitModal.slug]; setKitModal(null); setKitConfirmChecked(false); navigate(path); }}
                    disabled={!kitConfirmChecked}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="kit-lock-activate-included-btn"
                  >
                    Activar este kit (gratis con Business)
                  </Button>
                </>
              )}
              {kitModal.reason === 'requires_unlock' && (
                <Button onClick={handleUnlockKit} disabled={unlockingKit} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="kit-lock-unlock-btn">
                  {unlockingKit ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirigiendo a PayPal…</> : `Desbloquear por $${kitsStatus?.unlock_price_usd || 99} USD`}
                </Button>
              )}
              <Button variant="outline" onClick={() => { setKitModal(null); setKitConfirmChecked(false); }} data-testid="kit-lock-close-btn">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-home-link">
            <MessageSquare className="w-7 h-7 text-indigo-600" />
            <span className="text-xl font-bold tracking-tight text-gray-950">ACBotHub</span>
          </a>
          <div className="flex items-center gap-3">
            {hasBots && (
              <BotSelector
                bots={botList}
                selectedBot={selectedBot}
                onSelect={handleSelectBot}
                maxBots={maxBots}
                onCreateBot={handleCreateBot}
                isCreating={isCreating}
                isAdmin={isAdmin}
              />
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => navigate('/admin')} data-testid="admin-link"
                className="border-gray-200 text-gray-700 hover:bg-gray-50">
                <Shield className="w-4 h-4 mr-2" />Admin
              </Button>
            )}
            <Button variant="outline" onClick={() => navigateToKit('/kit', 'kit')} data-testid="kit-link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50">
              <Car className="w-4 h-4 mr-2" />
              {kitsStatus && !kitsStatus.kits?.find(k=>k.slug==='kit')?.unlocked && <Lock className="w-3.5 h-3.5 mr-1 text-gray-400" />}
              Kit AutoBot
            </Button>
            <Button variant="outline" onClick={() => navigateToKit('/garzon', 'garzon')} data-testid="garzon-link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50">
              <span className="mr-2">🍽️</span>
              {kitsStatus && !kitsStatus.kits?.find(k=>k.slug==='garzon')?.unlocked && <Lock className="w-3.5 h-3.5 mr-1 text-gray-400" />}
              GarzónBot
            </Button>
            <Button variant="outline" onClick={() => navigateToKit('/inmobiliaria', 'inmobiliaria')} data-testid="inmobiliaria-link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50">
              <span className="mr-2">🏠</span>
              {kitsStatus && !kitsStatus.kits?.find(k=>k.slug==='inmobiliaria')?.unlocked && <Lock className="w-3.5 h-3.5 mr-1 text-gray-400" />}
              Kit Inmobiliario
            </Button>
            <Button variant="outline" onClick={() => navigateToKit('/kit-legal', 'legal')} data-testid="legal-link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50">
              <span className="mr-2">⚖️</span>
              {kitsStatus && !kitsStatus.kits?.find(k=>k.slug==='legal')?.unlocked && <Lock className="w-3.5 h-3.5 mr-1 text-gray-400" />}
              Kit Legal
            </Button>
            <Button variant="outline" onClick={() => navigateToKit('/kit-store', 'store')} data-testid="store-link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50">
              <span className="mr-2">🛍️</span>
              {kitsStatus && !kitsStatus.kits?.find(k=>k.slug==='store')?.unlocked && <Lock className="w-3.5 h-3.5 mr-1 text-gray-400" />}
              Kit StoreBot
            </Button>
            <Button variant="outline" onClick={() => navigate('/billing')} data-testid="billing-link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50">
              <CreditCard className="w-4 h-4 mr-2" />Facturacion
            </Button>
            <NotificationBell />
            <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
              <User className="w-5 h-5 text-gray-500" />
              <span className="text-gray-700 font-medium">{user?.name || user?.email}</span>
            </div>
            <Button onClick={handleLogout} data-testid="logout-btn" variant="ghost" className="text-gray-700 hover:text-gray-950">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-gray-950 mb-2">Panel de Control</h1>
          <p className="text-gray-600">Administra la configuracion y ajustes de tus asistentes de WhatsApp</p>
          <div
            className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900"
            data-testid="kit-tutorial-global-notice"
          >
            Para ver cómo configurar tu Asistente 🚗 Kit AutoBot haz clic{' '}
            <button
              type="button"
              onClick={() => navigate('/kit')}
              data-testid="kit-tutorial-global-link"
              className="underline font-semibold text-amber-900 hover:text-amber-700"
            >
              Aquí
            </button>
          </div>
          <div
            className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-900"
            data-testid="garzon-tutorial-global-notice"
          >
            Para ver cómo configurar tu Asistente 🍽️ GarzónBot haz clic{' '}
            <button
              type="button"
              onClick={() => navigate('/garzon')}
              data-testid="garzon-tutorial-global-link"
              className="underline font-semibold text-emerald-900 hover:text-emerald-700"
            >
              Aquí
            </button>
          </div>
          <div
            className="mt-3 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 text-sm text-purple-900"
            data-testid="legal-tutorial-global-notice"
          >
            Para configurar tu Asistente ⚖️ Kit Legal (audiencias PJUD automáticas) haz clic{' '}
            <button
              type="button"
              onClick={() => navigate('/kit-legal')}
              data-testid="legal-tutorial-global-link"
              className="underline font-semibold text-purple-900 hover:text-purple-700"
            >
              Aquí
            </button>
          </div>
        </div>

        {/* Bot Cards View */}
        {viewMode === 'list' && (
          <div data-testid="bot-cards-view">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-gray-950">Mis Asistentes</h2>
                <p className="text-sm text-gray-500">{botList.length} de {maxBots >= 999 ? 'ilimitados' : maxBots} asistentes</p>
              </div>
              {canCreate && (
                <Button onClick={handleCreateBot} disabled={isCreating} data-testid="create-bot-from-list-btn"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg rounded-lg font-medium px-6 py-2.5">
                  {isCreating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : <><Plus className="w-4 h-4 mr-2" />Nuevo Asistente</>}
                </Button>
              )}
            </div>

            {botList.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 shadow-sm text-center" data-testid="no-bot-state">
                <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageSquare className="w-10 h-10 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-gray-950 mb-3">Aun No Tienes un Asistente</h2>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">Habla con Alex para generar tu prompt personalizado o crea tu asistente manualmente.</p>
                <Button onClick={handleCreateBot} disabled={isCreating} data-testid="create-bot-btn"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg rounded-lg font-medium px-8 py-3 text-lg">
                  {isCreating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creando...</> : <><Plus className="w-5 h-5 mr-2" />Crear Mi Primer Asistente</>}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {botList.map(bot => {
                  const status = bot.status || 'disconnected';
                  const isActive = status === 'connected';
                  const isWaiting = status === 'waiting_for_scan';
                  return (
                    <div key={bot.bot_id} onClick={() => handleSelectBot(bot)} data-testid={`bot-card-${bot.bot_id}`}
                      className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer group relative">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${bot.bot_type === 'kit' ? 'bg-amber-100 group-hover:bg-amber-200' : bot.bot_type === 'garzon' ? 'bg-emerald-100 group-hover:bg-emerald-200' : bot.bot_type === 'inmobiliaria' ? 'bg-violet-100 group-hover:bg-violet-200' : bot.bot_type === 'legal' ? 'bg-purple-100 group-hover:bg-purple-200' : bot.bot_type === 'store' ? 'bg-sky-100 group-hover:bg-sky-200' : 'bg-indigo-100 group-hover:bg-indigo-200'}`}>
                          {bot.bot_type === 'kit' ? (
                            <Car className="w-6 h-6 text-amber-600" />
                          ) : bot.bot_type === 'garzon' ? (
                            <span className="text-2xl">🍽️</span>
                          ) : bot.bot_type === 'inmobiliaria' ? (
                            <span className="text-2xl">🏠</span>
                          ) : bot.bot_type === 'legal' ? (
                            <span className="text-2xl">⚖️</span>
                          ) : bot.bot_type === 'store' ? (
                            <span className="text-2xl">🛍️</span>
                          ) : (
                            <Bot className="w-6 h-6 text-indigo-600" />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${bot.bot_type === 'kit' ? 'bg-amber-50 text-amber-700 border border-amber-200' : bot.bot_type === 'garzon' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : bot.bot_type === 'inmobiliaria' ? 'bg-violet-50 text-violet-700 border border-violet-200' : bot.bot_type === 'legal' ? 'bg-purple-50 text-purple-700 border border-purple-200' : bot.bot_type === 'store' ? 'bg-sky-50 text-sky-700 border border-sky-200' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'}`} data-testid={`bot-type-badge-${bot.bot_id}`}>
                            {bot.bot_type === 'kit' ? 'Kit AutoBot' : bot.bot_type === 'garzon' ? 'GarzónBot' : bot.bot_type === 'inmobiliaria' ? 'Kit Inmobiliario' : bot.bot_type === 'legal' ? 'Kit Legal' : bot.bot_type === 'store' ? 'StoreBot' : 'General'}
                          </span>
                          <span className={`w-2.5 h-2.5 rounded-full ${isActive ? 'bg-emerald-500' : isWaiting ? 'bg-amber-500' : 'bg-gray-300'}`}></span>
                          <span className={`text-xs font-medium ${isActive ? 'text-emerald-600' : isWaiting ? 'text-amber-600' : 'text-gray-500'}`}>
                            {isActive ? 'Conectado' : isWaiting ? 'Esperando QR' : 'Desconectado'}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-950 mb-1 group-hover:text-indigo-600 transition-colors">{bot.bot_name || 'Mi Asistente'}</h3>
                      <p className="text-sm text-gray-500 mb-3">{bot.business_name || 'Sin configurar'}</p>
                      {bot.bot_prompt && (
                        <p className="text-xs text-gray-400 line-clamp-2 mb-4">{bot.bot_prompt.substring(0, 100)}...</p>
                      )}
                      {bot.bot_type === 'kit' && (
                        <div
                          className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-xs text-amber-800"
                          data-testid={`kit-tutorial-notice-${bot.bot_id}`}
                        >
                          Para ver cómo configurar tu Asistente 🚗 Kit AutoBot haz clic{' '}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate('/kit'); }}
                            data-testid={`kit-tutorial-link-${bot.bot_id}`}
                            className="underline font-semibold text-amber-900 hover:text-amber-700"
                          >
                            Aquí
                          </button>
                        </div>
                      )}
                      {bot.bot_type === 'garzon' && (
                        <div
                          className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4 text-xs text-emerald-800"
                          data-testid={`garzon-tutorial-notice-${bot.bot_id}`}
                        >
                          Para ver cómo configurar tu Asistente 🍽️ GarzónBot haz clic{' '}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate('/garzon'); }}
                            data-testid={`garzon-tutorial-link-${bot.bot_id}`}
                            className="underline font-semibold text-emerald-900 hover:text-emerald-700"
                          >
                            Aquí
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400">Click para configurar</span>
                        <Button variant="ghost" size="sm"
                          onClick={(e) => handleDeleteBot(e, bot.bot_id, bot.bot_name)}
                          disabled={isDeleting === bot.bot_id}
                          data-testid={`delete-bot-${bot.bot_id}`}
                          className="text-red-500 hover:bg-red-50 h-8 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isDeleting === bot.bot_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Bot Configuration View */}
        {viewMode === 'config' && selectedBot && (
          <>

        {/* Back to list button */}
        <div className="mb-6">
          <Button variant="ghost" onClick={handleBackToList} data-testid="back-to-list-btn"
            className="text-gray-600 hover:text-gray-950 hover:bg-gray-100">
            <ArrowLeft className="w-4 h-4 mr-2" />Volver a Mis Asistentes
          </Button>
        </div>

        {/* Subscription Warning */}
        {!subscriptionActive && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8" data-testid="subscription-warning">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-amber-600" />
                <div>
                  <h3 className="font-semibold text-amber-800">{subscriptionMessage || 'Tu suscripcion ha expirado'}</h3>
                  <p className="text-sm text-amber-600">Tu asistente esta pausado. Renueva tu suscripcion para reactivarlo.</p>
                </div>
              </div>
              <Button onClick={() => navigate('/billing')} data-testid="renew-subscription-btn"
                className="bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md hover:shadow-lg rounded-lg font-medium px-6 py-2.5">
                Renovar Suscripcion
              </Button>
            </div>
          </div>
        )}

        {/* Status Cards - only when a bot is selected */}
        {selectedBot && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-950">Estado del Asistente</h3>
                  <span className={`status-dot ${getStatusDotClass()}`}></span>
                </div>
                <p className={`text-2xl font-bold ${getStatusColor()} mb-1`} data-testid="bot-status-text">{getStatusText()}</p>
                <p className="text-sm text-gray-500 mb-4 font-mono">{selectedBot.bot_name}</p>
                {!isConnected && !isWaitingQR && subscriptionActive && (
                  <Button onClick={handleStartWhatsApp} disabled={isConnecting} data-testid="connect-whatsapp-btn"
                    className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md hover:shadow-lg rounded-lg font-medium px-6 py-2.5">
                    {isConnecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activando...</> : <><Wifi className="w-4 h-4 mr-2" />Activar Asistente</>}
                  </Button>
                )}
                {(isConnected || isWaitingQR) && (
                  <Button onClick={handleStopWhatsApp} data-testid="disconnect-whatsapp-btn" variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50">
                    <WifiOff className="w-4 h-4 mr-2" />Desconectar WhatsApp
                  </Button>
                )}
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-950">ID del Bot</h3>
                  <Settings className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 font-mono bg-gray-50 px-3 py-2 rounded border border-gray-200" data-testid="bot-id">{selectedBot.bot_id}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-950">Control del Asistente</h3>
                  <Power className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">{selectedBot.is_enabled ? 'Habilitado' : 'Deshabilitado'}</span>
                  <Switch checked={selectedBot.is_enabled} onCheckedChange={handleToggleBot} data-testid="bot-toggle-switch" />
                </div>
              </div>
            </div>

            {/* QR Code */}
            {isWaitingQR && (
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm mb-8" data-testid="qr-code-container">
                <h2 className="text-2xl font-bold tracking-tight text-gray-950 mb-6 text-center">Escanea el Codigo QR para Conectar WhatsApp</h2>
                <div className="flex justify-center mb-6">
                  <div className="qr-container bg-white p-4 rounded-xl border-2 border-gray-100 shadow-inner">
                    {qrDataUrl ? (
                      <img src={qrDataUrl} alt="QR Code" className="w-64 h-64" data-testid="qr-code-image" />
                    ) : (
                      <div className="w-64 h-64 flex flex-col items-center justify-center gap-3">
                        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                        <p className="text-sm text-gray-500">Generando codigo QR...</p>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-center text-gray-600 mb-4">Abre WhatsApp &rarr; Dispositivos vinculados &rarr; Vincular dispositivo &rarr; Escanea este codigo</p>
                <div className="flex items-center justify-center gap-2 text-amber-600">
                  <RefreshCw className="w-4 h-4 animate-spin" /><span className="text-sm font-medium">Esperando escaneo...</span>
                </div>
              </div>
            )}

            {isConnected && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8" data-testid="connected-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center"><Wifi className="w-5 h-5 text-emerald-600" /></div>
                  <div><h3 className="text-lg font-semibold text-emerald-800">WhatsApp Conectado</h3><p className="text-sm text-emerald-600">Tu asistente esta activo y respondiendo mensajes.</p></div>
                </div>
              </div>
            )}

            {/* Metrics */}
            <div className="mb-8">
              <BotMetrics botId={selectedBot.bot_id} />
            </div>

            {/* Config + Chat */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm" data-testid="bot-config-panel">
                <h2 className="text-2xl font-bold tracking-tight text-gray-950 mb-6">Configuracion del Asistente</h2>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="bot-prompt" className="text-gray-700 font-medium text-base mb-2 block">Instrucciones del Asistente (Prompt)</Label>
                    <Textarea id="bot-prompt" value={botPrompt} onChange={(e) => setBotPrompt(e.target.value)} data-testid="bot-prompt-input"
                      placeholder="Eres un asistente util para un negocio." className="bg-white border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 text-gray-900 min-h-[100px]" />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium text-base mb-2 block">Proveedor de IA</Label>
                    <div className="flex gap-2 mb-4" data-testid="ai-provider-selector">
                      <button type="button" onClick={() => setAiProvider('openai')} data-testid="provider-openai-btn"
                        className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${aiProvider === 'openai' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                        OpenAI GPT-4o-mini
                      </button>
                      <button type="button" onClick={() => setAiProvider('anthropic')} data-testid="provider-anthropic-btn"
                        className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${aiProvider === 'anthropic' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                        Anthropic Claude Sonnet
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="openai-api-key" className="text-gray-700 font-medium text-base mb-2 block">
                      {aiProvider === 'anthropic' ? 'Anthropic API Key' : 'OpenAI API Key'}
                    </Label>
                    {hasApiKey && !openaiApiKey && (
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-sm text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded">Configurada</span>
                        <span className="text-xs text-gray-400 font-mono">{apiKeyMasked}</span>
                      </div>
                    )}
                    {!hasApiKey && (
                      <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="api-key-guide">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Settings className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-amber-800 mb-1">Tu asistente necesita una API Key para ser inteligente</p>
                            <p className="text-xs text-amber-700 mb-3">Sin una key de IA, tu bot solo enviara respuestas automaticas basicas. Sigue estos pasos:</p>
                            {aiProvider === 'anthropic' ? (
                              <ol className="text-xs text-amber-700 space-y-1.5 list-decimal list-inside">
                                <li>Ve a <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 font-semibold hover:underline">console.anthropic.com/settings/keys</a></li>
                                <li>Crea una cuenta o inicia sesion</li>
                                <li>Haz click en <span className="font-semibold">"Create Key"</span></li>
                                <li>Copia la key (empieza con <span className="font-mono bg-amber-100 px-1 rounded">sk-ant-...</span>)</li>
                                <li>Pegala abajo y guarda los cambios</li>
                              </ol>
                            ) : (
                              <ol className="text-xs text-amber-700 space-y-1.5 list-decimal list-inside">
                                <li>Ve a <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-semibold hover:underline">platform.openai.com/api-keys</a></li>
                                <li>Crea una cuenta o inicia sesion</li>
                                <li>Haz click en <span className="font-semibold">"Create new secret key"</span></li>
                                <li>Copia la key (empieza con <span className="font-mono bg-amber-100 px-1 rounded">sk-proj-...</span>)</li>
                                <li>Pegala abajo y guarda los cambios</li>
                              </ol>
                            )}
                            <p className="text-xs text-amber-600 mt-2 italic">Tu key se guarda encriptada y nunca se comparte.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <Input id="openai-api-key" type="password" value={openaiApiKey} onChange={(e) => setOpenaiApiKey(e.target.value)} data-testid="openai-api-key-input"
                      placeholder={hasApiKey ? "Dejar vacio para mantener la actual..." : (aiProvider === 'anthropic' ? "sk-ant-..." : "sk-proj-...")}
                      className="bg-white border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 text-gray-900 font-mono text-sm" />
                    {hasApiKey && (
                      <p className="mt-1 text-sm text-gray-500">
                        {aiProvider === 'anthropic' ? (
                          <>Obten tu key en <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">console.anthropic.com</a></>
                        ) : (
                          <>Obten tu key en <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">platform.openai.com</a></>
                        )}
                        {' — Se guarda encriptada.'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium text-base mb-2 block">Nombre del Negocio</Label>
                    <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="business-name-input" placeholder="ej., Mi Empresa" className="bg-white border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 text-gray-900" />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium text-base mb-2 block">Servicios</Label>
                    <Textarea value={services} onChange={(e) => setServices(e.target.value)} data-testid="services-input" placeholder="ej., Diseno web, Apps" className="bg-white border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 text-gray-900" />
                  </div>
                  {/* Business Plan: Order Notification System */}
                  {(userPlanId === 'business' || isAdmin) && (
                    <div>
                      <MenuEditor
                        shippingCost={shippingCost}
                        setShippingCost={setShippingCost}
                        notificationWhatsapp={notificationWhatsapp}
                        setNotificationWhatsapp={setNotificationWhatsapp}
                      />
                    </div>
                  )}
                  <div className="pt-4">
                    <Button onClick={handleSaveConfig} disabled={isSaving} data-testid="save-config-btn"
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg rounded-lg font-medium px-8 py-2.5">
                      {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : <><Save className="w-4 h-4 mr-2" />Guardar Configuracion</>}
                    </Button>
                  </div>
                </div>
              </div>
              <ChatSimulator bot={selectedBot} />
            </div>

            {/* Prompt History */}
            <div className="mb-8">
              <PromptHistory botId={selectedBot.bot_id} />
            </div>

            {/* Conversations */}
            <div className="mb-8">
              <ConversationPanel botId={selectedBot.bot_id} />
            </div>

            {/* Recent Messages */}
            <div className="mb-8">
              <RecentMessages botId={selectedBot.bot_id} />
            </div>
          </>
        )}

          </>
        )}

      </main>

      {/* Bot Type Selection Modal */}
      {showCreateTypeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="create-type-modal" onClick={() => setShowCreateTypeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6">
              <h2 className="text-2xl font-bold tracking-tight text-gray-950 mb-2">Elige el tipo de asistente</h2>
              <p className="text-sm text-gray-600">Selecciona la plantilla que mejor se adapte a tu negocio. Podras configurarlo en detalle despues.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <button
                onClick={() => confirmCreateBot('general')}
                disabled={isCreating}
                data-testid="select-type-general-btn"
                className="group relative flex flex-col text-left p-5 h-72 rounded-xl border-2 border-gray-200 hover:border-indigo-500 hover:bg-indigo-50/40 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-100 group-hover:bg-indigo-200 flex items-center justify-center mb-3 transition-colors shrink-0">
                  <Bot className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-950 mb-1 leading-tight">Asistente General</h3>
                <span className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 mb-2">Personalizable</span>
                <p className="text-xs text-gray-600 leading-relaxed overflow-hidden">Bot para cualquier negocio: tiendas, servicios, restaurantes, profesionales. Configura tu prompt y conecta WhatsApp.</p>
              </button>
              <button
                onClick={() => confirmCreateBot('kit')}
                disabled={isCreating}
                data-testid="select-type-kit-btn"
                className="group relative flex flex-col text-left p-5 h-72 rounded-xl border-2 border-gray-200 hover:border-amber-500 hover:bg-amber-50/40 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-100 group-hover:bg-amber-200 flex items-center justify-center mb-3 transition-colors shrink-0">
                  <Car className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-950 mb-1 leading-tight">Kit AutoBot</h3>
                <span className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 mb-2">Concesionarias</span>
                <p className="text-xs text-gray-600 leading-relaxed overflow-hidden">Plantilla para concesionarias: captura leads, score automático, agenda pruebas de manejo y notifica por email.</p>
              </button>
              <button
                onClick={() => confirmCreateBot('garzon')}
                disabled={isCreating}
                data-testid="select-type-garzon-btn"
                className="group relative flex flex-col text-left p-5 h-72 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50/40 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center mb-3 transition-colors shrink-0">
                  <span className="text-2xl">🍽️</span>
                </div>
                <h3 className="text-base font-semibold text-gray-950 mb-1 leading-tight">GarzónBot</h3>
                <span className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 mb-2">Restaurantes</span>
                <p className="text-xs text-gray-600 leading-relaxed overflow-hidden">Asistente para bares y restaurantes: toma pedidos por mesa vía QR y envía comandas a Google Sheets.</p>
              </button>
              <button
                onClick={() => confirmCreateBot('inmobiliaria')}
                disabled={isCreating}
                data-testid="select-type-inmobiliaria-btn"
                className="group relative flex flex-col text-left p-5 h-72 rounded-xl border-2 border-gray-200 hover:border-violet-500 hover:bg-violet-50/40 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-violet-100 group-hover:bg-violet-200 flex items-center justify-center mb-3 transition-colors shrink-0">
                  <span className="text-2xl">🏠</span>
                </div>
                <h3 className="text-base font-semibold text-gray-950 mb-1 leading-tight">Kit Inmobiliario</h3>
                <span className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 mb-2">Corredoras</span>
                <p className="text-xs text-gray-600 leading-relaxed overflow-hidden">Plantilla para corredoras: distingue buscadores y propietarios, agenda visitas y clasifica por temperatura del lead.</p>
              </button>
              <button
                onClick={() => confirmCreateBot('legal')}
                disabled={isCreating}
                data-testid="select-type-legal-btn"
                className="group relative flex flex-col text-left p-5 h-72 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50/40 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-100 group-hover:bg-purple-200 flex items-center justify-center mb-3 transition-colors shrink-0">
                  <span className="text-2xl">⚖️</span>
                </div>
                <h3 className="text-base font-semibold text-gray-950 mb-1 leading-tight">Kit Legal</h3>
                <span className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 mb-2">Estudios jurídicos</span>
                <p className="text-xs text-gray-600 leading-relaxed overflow-hidden">Para estudios jurídicos: agenda clientes, detecta audiencias PJUD automáticamente y las agrega a Google Calendar.</p>
              </button>
              <button
                onClick={() => confirmCreateBot('store')}
                disabled={isCreating}
                data-testid="select-type-store-btn"
                className="group relative flex flex-col text-left p-5 h-72 rounded-xl border-2 border-gray-200 hover:border-sky-500 hover:bg-sky-50/40 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-sky-100 group-hover:bg-sky-200 flex items-center justify-center mb-3 transition-colors shrink-0">
                  <span className="text-2xl">🛍️</span>
                </div>
                <h3 className="text-base font-semibold text-gray-950 mb-1 leading-tight">StoreBot</h3>
                <span className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 mb-2">Tiendas con catálogo</span>
                <p className="text-xs text-gray-600 leading-relaxed overflow-hidden">Asistente para cualquier negocio con catálogo y precios: showrooms, ferreterías, mueblerías, distribuidores. Responde consultas, entrega precios y captura leads automáticamente.</p>
              </button>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateTypeModal(false)}
                data-testid="cancel-create-type-btn"
                className="border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
            </div>
            {isCreating && (
              <div className="absolute inset-0 bg-white/60 rounded-2xl flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
